import {
  useDeleteEmployeeAttachmentMutation,
  useGetEmployeeAttachmentsQuery,
} from '@/app/features/employee/emp-dashboard/emp-dashboard.api';
import {
  DOCUMENT_TYPES,
  type EmployeeAttachment,
} from '@/app/features/employee/emp-dashboard/emp-dashboard.type';
import ConfirmationModal from '@/components/ConfirmationModal';
import CustomTable from '@/components/CustomTable';
import useDiscloser from '@/hooks/useDiscloser';
import useFilters from '@/hooks/useFilters';
import {
  ChevronDownIcon,
  CirclePlusIcon,
  EyeIcon,
  SearchIcon,
  TrashIcon,
} from '@/icons';
import { getErrorMessage } from '@/lib/error';
import { toast } from '@/lib/toast';
import { formatDate } from '@/utils';
import { Button, Flex, Input, Select, Space, Tag, type TableProps } from 'antd';
import ManageAttachment from './ManageAttachment';

type Props = {
  employeeId?: string;
};

const typeLabel = (value: string) =>
  DOCUMENT_TYPES.find((type) => type.value === value)?.label || value;

/** The list endpoint answers with everything at once, so paging is local. */
const PAGE_SIZE = 10;

const uploaderName = (value: EmployeeAttachment['uploadedBy']) => {
  if (!value) return 'n/a';
  return typeof value === 'string' ? 'n/a' : value.name || 'n/a';
};

/**
 * Employee details → Attachments.
 *
 * Everything filed about one employee — NID, contract, CV — in one list, with a
 * name and a type on every row so it stays searchable as the list grows.
 */
const Attachments = ({ employeeId }: Props) => {
  const [config, handleOpen, handleClose] = useDiscloser();
  const { page, keyword, filter, handleSearch, handleFilter, handlePagination } =
    useFilters<{ type?: string }>({ hideSkipLimit: true });

  const { data, isLoading, isFetching } = useGetEmployeeAttachmentsQuery(
    { employeeId: String(employeeId) },
    { skip: !employeeId },
  );

  const [deleteAttachment, { isLoading: isDeleting }] =
    useDeleteEmployeeAttachmentMutation();

  const handleDelete = async () => {
    try {
      await deleteAttachment({
        employeeId: String(employeeId),
        id: config?.data?.id,
      }).unwrap();
      toast('success', 'Document removed successfully');
      handleClose();
    } catch (error) {
      toast('error', getErrorMessage(error, 'Failed to remove the document'));
    }
  };

  // Search and type filter are applied here; the endpoint takes no params.
  const search = (keyword || '').toLowerCase();
  const rows = (data?.data || [])
    .filter((row) => !filter?.type || row.documentType === filter.type)
    .filter((row) =>
      search ? (row.documentName || '').toLowerCase().includes(search) : true,
    );
  const pageRows = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const columns: TableProps<EmployeeAttachment>['columns'] = [
    {
      title: 'Document Name',
      dataIndex: 'documentName',
      render: (value: string) => value || 'n/a',
    },
    {
      title: 'Type',
      dataIndex: 'documentType',
      render: (value: string) => (
        <Tag className="!bg-transparent !border-theme-gray-700 !text-white !rounded">
          {typeLabel(value)}
        </Tag>
      ),
    },
    {
      title: 'File',
      dataIndex: 'fileName',
      render: (value: string) => (
        <span className="text-white/60">{value || 'n/a'}</span>
      ),
    },
    {
      title: 'Uploaded By',
      dataIndex: 'uploadedBy',
      render: (value: EmployeeAttachment['uploadedBy']) => uploaderName(value),
    },
    {
      title: 'Uploaded On',
      // The list response leaves out `createdAt` (only the upload response has
      // it). A document is never edited after upload, so `updatedAt` is the
      // same moment.
      render: (_, record) => {
        const uploadedAt = record.createdAt || record.updatedAt;
        return uploadedAt ? formatDate(uploadedAt, 'DD MMM, YYYY') : 'n/a';
      },
    },
    {
      title: 'Actions',
      align: 'end',
      render: (_, record) => (
        <Space>
          {/* Opens the stored file in a new tab; the browser downloads what it cannot show */}
          <a href={record.fileUrl} target="_blank" rel="noreferrer">
            <Button
              size="small"
              className="action-btn"
              icon={<EyeIcon size={20} />}
            />
          </a>
          <Button
            size="small"
            className="action-btn"
            icon={<TrashIcon size={20} />}
            onClick={() => handleOpen({ type: 'DELETE', data: record })}
          />
        </Space>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <Flex align="center" justify="space-between" gap={16} wrap>
        <Flex align="center" gap={16} wrap>
          <Input
            size="large"
            onChange={handleSearch}
            placeholder="Search document"
            className="shrink-0 w-[225px]"
            prefix={<SearchIcon size={24} />}
          />
          <Select
            allowClear
            size="large"
            placeholder="Type"
            style={{ flexShrink: 0, width: 160 }}
            suffixIcon={<ChevronDownIcon size={24} className="text-white" />}
            value={filter?.type}
            onChange={(value: string) => handleFilter('type', value)}
            options={[...DOCUMENT_TYPES]}
          />
        </Flex>

        <Button
          size="large"
          disabled={!employeeId}
          onClick={() => handleOpen({ type: 'ADD' })}
          className="btn-primary !text-theme-gray-650 !font-bold hover:!text-theme-gray-650 !rounded-md"
        >
          <CirclePlusIcon size={20} />
          Upload Document
        </Button>
      </Flex>

      <CustomTable
        page={page}
        limit={PAGE_SIZE}
        hideRowSelect
        columns={columns}
        dataSource={pageRows}
        total={rows.length}
        loading={isLoading || isFetching}
        onPaginate={handlePagination}
        tableRootClassName="border-0 p-0"
      />

      <ManageAttachment
        open={config.open && config.type === 'ADD'}
        employeeId={String(employeeId)}
        handleClose={handleClose}
      />

      <ConfirmationModal
        open={config.open && config.type === 'DELETE'}
        handleClose={handleClose}
        handleSubmit={handleDelete}
        loading={isDeleting}
        title="Remove Document"
        subTitle={`Remove "${config?.data?.documentName}"? This cannot be undone.`}
        buttonName="Remove"
      />
    </div>
  );
};

export default Attachments;
